import { CHUNK_H_M, CHUNK_W_M } from "../config/constants";
import { SPAWN_CATEGORIES, SPAWN_CATEGORY, type SpawnCategory } from "../config/spawnCategories";
import type { DifficultyParams, EntityDescriptor, SpawnEntry, ZoneConfig, ZoneId } from "../config/types";
import { hash, mulberry32, type Rng } from "../core/rng";
import type { Mountain } from "./mountain";
import { buildSetPiece } from "./setPieces";

export interface ChunkContext {
    mountain: Mountain;
    zones: Record<ZoneId, ZoneConfig>;
    courseEndM: number | null;
    difficultyAt: (yM: number) => DifficultyParams;
}

export const MIN_SPACING_M = 3;
export const SAFE_START_M = 30;
export const TRAIL_CLEAR_M = 1.2;
const PLACE_TRIES = 30;

interface Point {
    x: number;
    y: number;
}

function pickWeighted(table: SpawnEntry[], rng: Rng): SpawnEntry {
    const total = table.reduce((sum, entry) => sum + entry.weight, 0);
    let roll = rng() * total;
    for (const entry of table) {
        roll -= entry.weight;
        if (roll <= 0) return entry;
    }
    return table[table.length - 1];
}

function randomCount(mean: number, rng: Rng): number {
    const n = mean * (0.75 + rng() * 0.5);
    return Math.floor(n) + (rng() < n % 1 ? 1 : 0);
}

function spawnEntryAt(ctx: ChunkContext, category: SpawnCategory, y: number, rng: Rng): SpawnEntry | null {
    const info = ctx.mountain.zoneAt(y);
    const zone = info.next && rng() < info.blend ? info.next : info.zone;
    const table = ctx.zones[zone].spawnTable.filter((entry) => SPAWN_CATEGORY[entry.key] === category);
    return table.length ? pickWeighted(table, rng) : null;
}

function freePoint(ctx: ChunkContext, left: number, top: number, taken: Point[], rng: Rng): Point | null {
    const margin = MIN_SPACING_M / 2;
    for (let attempt = 0; attempt < PLACE_TRIES; attempt++) {
        const p = {
            x: left + margin + rng() * (CHUNK_W_M - 2 * margin),
            y: top + margin + rng() * (CHUNK_H_M - 2 * margin),
        };
        if (isFree(ctx, p, taken)) return p;
    }
    return null;
}

function isFree(ctx: ChunkContext, p: Point, taken: Point[]): boolean {
    if (p.y < SAFE_START_M) return false;
    if (ctx.courseEndM !== null && p.y >= ctx.courseEndM) return false;
    const trail = ctx.mountain.trailAt(p.y);
    if (trail && Math.abs(p.x - trail.x) < TRAIL_CLEAR_M) return false;
    return taken.every((t) => Math.hypot(t.x - p.x, t.y - p.y) >= MIN_SPACING_M);
}

function finishLine(ctx: ChunkContext, left: number, top: number): EntityDescriptor[] {
    const end = ctx.courseEndM;
    if (end === null || end < top || end >= top + CHUNK_H_M) return [];
    const x = ctx.mountain.trailAt(end)?.x ?? 0;
    return x >= left && x < left + CHUNK_W_M ? [{ type: "finish_banner", x, y: end }] : [];
}

function setPiece(ctx: ChunkContext, left: number, top: number, rng: Rng): EntityDescriptor[] {
    const y = top + CHUNK_H_M / 2;
    if (ctx.courseEndM !== null && y + CHUNK_H_M / 2 >= ctx.courseEndM) return [];
    const names = ctx.zones[ctx.mountain.zoneAt(y).zone].setPieces;
    if (!names.length || rng() >= ctx.difficultyAt(y).setPieceWeight) return [];

    const x = ctx.mountain.trailAt(y)?.x ?? left + CHUNK_W_M / 2;
    if (x < left || x >= left + CHUNK_W_M || y < SAFE_START_M) return [];
    return buildSetPiece(names[Math.floor(rng() * names.length)], x, y);
}

export function generateChunk(ctx: ChunkContext, cx: number, cy: number): EntityDescriptor[] {
    const rng = mulberry32(hash(ctx.mountain.seed, cx, cy));
    const left = cx * CHUNK_W_M;
    const top = cy * CHUNK_H_M;

    const out = [...finishLine(ctx, left, top), ...setPiece(ctx, left, top, rng)];
    const taken: Point[] = [...out];
    const difficulty = ctx.difficultyAt(top + CHUNK_H_M / 2);

    for (const category of SPAWN_CATEGORIES) {
        const count = randomCount(difficulty[category], rng);
        for (let i = 0; i < count; i++) {
            const p = freePoint(ctx, left, top, taken, rng);
            const entry = p && spawnEntryAt(ctx, category, p.y, rng);
            if (!p || !entry) continue;
            out.push({ type: entry.key, variant: entry.variant, ...p });
            taken.push(p);
        }
    }
    return out;
}
